/**
 * Descuentos (DISENO, «Descuentos»; docs/activa/FASE-3-PLAN.md). Del equipo, bajo «Tu marca». Las reglas que el agente
 * nunca pasa: el descuento máximo, el margen que la marca cuida, la compra mínima habitual y los descuentos que el
 * comercio ya da. Y la medición con la que arranca cada campaña: el % de control (Segmentar empieza con ese) y cuántos
 * días se mide.
 *
 * Mientras la marca no guarde Descuentos, las campañas nuevas no ofrecen descuento: la pantalla lo dice arriba.
 */

import { useState, type FormEvent, type KeyboardEvent } from "react";
import { Loader2, Plus, X } from "lucide-react";
import { usePedido } from "../../emails/hooks/usePedido";
import { llamar } from "../../wa-oficial/components/base/comun";
import { ErrorTarjeta, Esqueleto } from "../../wa-oficial/components/estadisticas/comun";
import { textoError } from "../../wa-oficial/logic/respuesta";
import { Opciones } from "../../segmentaciones/components/Opciones";
import { btnPrimario, btnSecundario } from "../../segmentaciones/components/PasosCampana";
import { EncabezadoPagina, Pagina } from "../../../components/activa/Pagina";
import { fecha, pesos } from "../../segmentaciones/logic/segmentar";
import { num } from "../../campana-nueva/utils/format";
import { descuentosApi } from "../api";
import {
  AYUDA_CONTROL,
  CONTROLES,
  DIAS_MEDICION,
  MAXIMOS,
  diaDe,
  formularioDe,
  hoyLocal,
  problemaDelVigente,
  textoVigencia,
  validarFormulario,
  type Campo,
  type Formulario,
} from "../logic/descuentos";
import type { DescuentoVigente, Descuentos } from "../types";

const campoClase = (conError: boolean) =>
  `min-h-[44px] w-full rounded-xl border bg-white px-3 text-sm text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 ${conError ? "border-red-400" : "border-slate-200"}`;
const tarjeta = "rounded-2xl border border-slate-200 bg-white p-5 space-y-5";
const ayuda = "text-sm text-slate-600";

/** «Descuentos que ya tenés»: la lista y el formulario chico para sumar uno. */
function DescuentosQueYaTenes({ vigentes, onCambio }: { vigentes: (DescuentoVigente & { vencido?: boolean })[]; onCambio: (v: DescuentoVigente[]) => void }) {
  const [abierto, setAbierto] = useState(false);
  const [nuevo, setNuevo] = useState<DescuentoVigente>({ nombre: "", condicion: "", hasta: null });
  const [conFecha, setConFecha] = useState(false);
  const [problema, setProblema] = useState("");

  const agregar = () => {
    const v = { nombre: nuevo.nombre.trim().replace(/\s+/g, " "), condicion: nuevo.condicion.trim(), hasta: conFecha ? nuevo.hasta || "" : null };
    const p = problemaDelVigente(v, vigentes);
    if (p) return setProblema(p);
    onCambio([...vigentes.map(({ nombre, condicion, hasta }) => ({ nombre, condicion, hasta })), v]);
    setNuevo({ nombre: "", condicion: "", hasta: null });
    setConFecha(false);
    setProblema("");
    setAbierto(false);
  };
  // Enter en el formulario chico agrega el descuento; no guarda toda la pantalla
  const enter = (e: KeyboardEvent) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    agregar();
  };

  return (
    <section aria-labelledby="vigentes-titulo" className="space-y-3">
      <h3 id="vigentes-titulo" className="text-sm font-semibold text-slate-900">
        Descuentos que ya tenés
      </h3>
      {vigentes.length === 0 && <p className={ayuda}>Ninguno cargado.</p>}
      {vigentes.length > 0 && (
        <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200" data-testid="vigentes">
          {vigentes.map((v) => (
            <li key={v.nombre} className="flex items-center gap-3 px-4 py-2">
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-slate-900">{v.nombre}</p>
                {v.condicion && <p className="text-sm text-slate-600">{v.condicion}</p>}
              </div>
              <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${v.vencido ? "bg-red-50 text-red-700" : "bg-slate-100 text-slate-600"}`}>{textoVigencia(v)}</span>
              <button
                type="button"
                aria-label={`Sacar «${v.nombre}»`}
                onClick={() => onCambio(vigentes.filter((x) => x.nombre !== v.nombre).map(({ nombre, condicion, hasta }) => ({ nombre, condicion, hasta })))}
                className="inline-flex h-11 w-11 flex-none items-center justify-center rounded-xl text-slate-500 hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400"
              >
                <X size={16} aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}
      {abierto ? (
        <div className="space-y-3 rounded-xl border border-violet-200 bg-violet-50/40 p-4" data-testid="nuevo-vigente">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block space-y-1 text-sm">
              <span className="font-medium text-slate-900">Nombre</span>
              <input value={nuevo.nombre} maxLength={60} onChange={(e) => setNuevo({ ...nuevo, nombre: e.target.value })} onKeyDown={enter} placeholder="Ej: 20% OFF TAKE AWAY" className={campoClase(false)} />
            </label>
            <label className="block space-y-1 text-sm">
              <span className="font-medium text-slate-900">Condición</span>
              <input value={nuevo.condicion} maxLength={80} onChange={(e) => setNuevo({ ...nuevo, condicion: e.target.value })} onKeyDown={enter} placeholder="Ej: retirando en el local" className={campoClase(false)} />
            </label>
          </div>
          <Opciones
            nombre="vigencia-nueva"
            leyenda="Cuánto dura"
            opciones={["siempre", "hasta"] as const}
            valor={conFecha ? "hasta" : "siempre"}
            onCambio={(o) => setConFecha(o === "hasta")}
            etiqueta={(o) => (o === "siempre" ? "Siempre activo" : "Hasta una fecha")}
          />
          {conFecha && (
            <label className="block max-w-xs space-y-1 text-sm">
              <span className="font-medium text-slate-900">Vale hasta el</span>
              <input type="date" min={hoyLocal()} value={nuevo.hasta ?? ""} onChange={(e) => setNuevo({ ...nuevo, hasta: e.target.value })} onKeyDown={enter} className={campoClase(false)} />
            </label>
          )}
          {problema && (
            <p role="alert" className="text-sm font-medium text-red-700">
              {problema}
            </p>
          )}
          <div className="flex flex-wrap gap-3">
            <button type="button" className={btnPrimario} onClick={agregar}>
              Agregar
            </button>
            <button
              type="button"
              className={btnSecundario}
              onClick={() => {
                setAbierto(false);
                setProblema("");
              }}
            >
              Cancelar
            </button>
          </div>
        </div>
      ) : (
        <button type="button" className={btnSecundario} onClick={() => setAbierto(true)}>
          <Plus size={16} aria-hidden="true" />
          Agregar
        </button>
      )}
      <p className={ayuda}>El cupón se suma arriba. El mensaje nombra cada uno por separado, nunca un total.</p>
    </section>
  );
}

function FormularioDescuentos({ inicial }: { inicial: Descuentos }) {
  const [datos, setDatos] = useState(inicial);
  const [f, setF] = useState<Formulario>(() => formularioDe(inicial));
  const [errores, setErrores] = useState<Partial<Record<Campo, string>>>({});
  const [error, setError] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [listo, setListo] = useState(false);
  const cambiar = (c: Partial<Formulario>) => {
    setF((x) => ({ ...x, ...c }));
    setListo(false);
  };
  const vencidoDe = new Map(datos.vigentes.map((v) => [v.nombre, v.vencido]));

  const guardar = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setListo(false);
    const v = validarFormulario(f);
    if (!v.ok) return setErrores(v.errores);
    setErrores({});
    setGuardando(true);
    const l = await llamar<Descuentos>(() => descuentosApi.guardar(v.reglas));
    setGuardando(false);
    if (l.ok) {
      setDatos(l.data);
      setF(formularioDe(l.data));
      setListo(true);
      return;
    }
    const campo = typeof l.extra.campo === "string" ? l.extra.campo : "";
    if (campo === "maximo" || campo === "margen" || campo === "compraMinima") setErrores({ [campo]: textoError(l) });
    else setError(textoError(l));
  };

  return (
    <form onSubmit={guardar} noValidate className="space-y-6">
      {!datos.guardado && (
        <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="sin-guardar">
          Todavía no guardaste los descuentos de esta marca. Hasta que los guardes, las campañas nuevas no ofrecen descuento.
        </p>
      )}

      <section className={tarjeta} aria-label="Reglas">
        <div className="space-y-2">
          <Opciones
            nombre="maximo"
            leyenda="Descuento máximo"
            opciones={MAXIMOS}
            valor={f.maximo}
            onCambio={(o) => cambiar({ maximo: o })}
            etiqueta={(o) => `${o}%`}
            ayudaId="maximo-ayuda"
            invalida={!!errores.maximo}
          />
          <p id="maximo-ayuda" className={ayuda}>
            A los que compran seguido les ofrece menos, o nada. Al que hace mucho que no viene, hasta este máximo.
          </p>
          {errores.maximo && (
            <p role="alert" className="text-sm font-medium text-red-700">
              {errores.maximo}
            </p>
          )}
        </div>

        <div className="space-y-1.5">
          <label htmlFor="margen" className="block text-sm font-semibold text-slate-900">
            Margen que querés cuidar
          </label>
          <div className="flex max-w-[180px] items-center gap-2">
            <input
              id="margen"
              inputMode="numeric"
              value={f.margen}
              onChange={(e) => cambiar({ margen: e.target.value })}
              placeholder="Ej: 35"
              aria-invalid={!!errores.margen || undefined}
              aria-describedby={errores.margen ? "margen-ayuda margen-error" : "margen-ayuda"}
              className={campoClase(!!errores.margen)}
            />
            <span className="text-sm font-semibold text-slate-600">%</span>
          </div>
          <p id="margen-ayuda" className={ayuda}>
            Con esto, el agente no regala margen donde no hace falta: ningún descuento lo pasa.
          </p>
          {errores.margen && (
            <p id="margen-error" role="alert" className="text-sm font-medium text-red-700">
              {errores.margen}
            </p>
          )}
        </div>

        <div className="space-y-1.5">
          <label htmlFor="compra-minima" className="block text-sm font-semibold text-slate-900">
            Compra mínima habitual
          </label>
          <div className="flex max-w-[220px] items-center gap-2">
            <span className="text-sm font-semibold text-slate-600">$</span>
            <input
              id="compra-minima"
              inputMode="numeric"
              value={f.compraMinima}
              onChange={(e) => cambiar({ compraMinima: e.target.value })}
              placeholder="Sin mínimo"
              aria-invalid={!!errores.compraMinima || undefined}
              aria-describedby={errores.compraMinima ? "minima-ayuda minima-error" : "minima-ayuda"}
              className={campoClase(!!errores.compraMinima)}
            />
          </div>
          <p id="minima-ayuda" className={ayuda}>
            {datos.ticketMediano
              ? `Tu ticket mediano es ${pesos(datos.ticketMediano.monto)} (${num(datos.ticketMediano.pedidos)} ${datos.ticketMediano.pedidos === 1 ? "pedido" : "pedidos"} del ${fecha(datos.ticketMediano.desde)} al ${fecha(datos.ticketMediano.hasta)}). `
              : ""}
            Dejala vacía si no pedís compra mínima.
          </p>
          {errores.compraMinima && (
            <p id="minima-error" role="alert" className="text-sm font-medium text-red-700">
              {errores.compraMinima}
            </p>
          )}
        </div>

        <DescuentosQueYaTenes vigentes={f.vigentes.map((v) => ({ ...v, vencido: vencidoDe.get(v.nombre) }))} onCambio={(vigentes) => cambiar({ vigentes })} />
      </section>

      <section className={tarjeta} aria-labelledby="medicion-titulo">
        <div className="space-y-1">
          <h2 id="medicion-titulo" className="text-base font-semibold text-slate-900">
            Medición
          </h2>
          <p className={ayuda}>Una parte de cada grupo no recibe nada. Así sabemos cuánto vendiste de más.</p>
        </div>
        <div className="space-y-2">
          <Opciones nombre="control" leyenda="Grupo de control" opciones={CONTROLES} valor={f.pctControl} onCambio={(o) => cambiar({ pctControl: o })} etiqueta={(o) => `${o}%`} ayudaId="control-ayuda" />
          <p id="control-ayuda" className={ayuda}>
            {AYUDA_CONTROL[f.pctControl]} Lo podés cambiar en cada campaña.
          </p>
        </div>
        <div className="space-y-1.5">
          <label htmlFor="dias-medicion" className="block text-sm font-semibold text-slate-900">
            Cuántos días medimos
          </label>
          <select
            id="dias-medicion"
            value={f.diasMedicion}
            onChange={(e) => cambiar({ diasMedicion: Number(e.target.value) })}
            className="min-h-[44px] rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400"
          >
            {DIAS_MEDICION.map((d) => (
              <option key={d} value={d}>
                {d} días
              </option>
            ))}
          </select>
        </div>
      </section>

      {error && (
        <p role="alert" className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <button type="submit" className={btnPrimario} disabled={guardando}>
          {guardando && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
          Guardar
        </button>
        {listo && (
          <p role="status" className="text-sm text-emerald-700">
            Descuentos guardados.
          </p>
        )}
        {!listo && datos.actualizado && (
          <p className="text-sm text-slate-500">
            Guardado {datos.actualizado.autor?.nombre ? `por ${datos.actualizado.autor.nombre} ` : ""}el {diaDe(datos.actualizado.at)}
          </p>
        )}
      </div>
    </form>
  );
}

export default function DescuentosPage() {
  const pedido = usePedido<Descuentos>(() => descuentosApi.ver(), "descuentos");
  const d = pedido.dato;
  return (
    <Pagina angosta>
      <EncabezadoPagina antetitulo="Tu marca" titulo="Descuentos" bajada="Las reglas que el agente nunca pasa." />
      {pedido.error && !d && <ErrorTarjeta texto={pedido.error} onReintentar={pedido.recargar} />}
      {pedido.cargando && !d && <Esqueleto alto="h-64" etiqueta="Cargando los descuentos" />}
      {d && <FormularioDescuentos inicial={d} />}
    </Pagina>
  );
}
